import { useCallback } from 'react';
import { WizardStep, WizardSubmit } from 'components/multiStepModal/types';

interface UseWizardPrimaryActionArgs<TData, TVariables> {
  currentStep: number;
  isFinalStep: boolean;
  step?: WizardStep;
  requestStepChange: (nextStep: number) => void;
  submit?: WizardSubmit<TData, TVariables>;
  fallbackSubmitErrorMessage: string;
}

export default function useWizardPrimaryAction<TData, TVariables>({
  currentStep,
  isFinalStep,
  step,
  requestStepChange,
  submit,
  fallbackSubmitErrorMessage,
}: UseWizardPrimaryActionArgs<TData, TVariables>) {
  const submitError = submit?.mutation.error;
  const submitErrorMessage = submitError?.message
    ? submitError.message
    : submitError
      ? fallbackSubmitErrorMessage
      : undefined;

  const errorMessage =
    (isFinalStep ? submitErrorMessage : undefined) ?? step?.next?.error;
  const loading = isFinalStep
    ? submit?.mutation.isPending
    : step?.next?.loading;
  const disabled = !!loading || step?.next?.enabled === false;

  const handlePrimaryAction = useCallback(async () => {
    if (!step || loading || disabled) {
      return;
    }

    if (isFinalStep && submit) {
      try {
        const variables = submit.getVariables
          ? await submit.getVariables()
          : (undefined as TVariables);

        if (variables === undefined && submit.getVariables) {
          return;
        }

        const data = await submit.mutation.mutateAsync(variables as TVariables);
        submit.onSuccess?.(data);
      } catch (error) {
        submit.onError?.(error as Error);
      }
      return;
    }

    try {
      const actionResult = step.next?.onClick
        ? await step.next.onClick()
        : true;
      if (actionResult === false) {
        return;
      }

      if (!isFinalStep) {
        // Step navigation is parent-controlled. The modal only requests changes.
        requestStepChange(currentStep + 1);
      }
    } catch (error) {
      // Step-level handlers are responsible for surfacing their own errors.
    }
  }, [
    currentStep,
    disabled,
    isFinalStep,
    loading,
    requestStepChange,
    step,
    submit,
  ]);

  return {
    disabled,
    loading,
    errorMessage,
    handlePrimaryAction,
  };
}
